import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import "../App.css";
import "../components/ScreenplayScriptWorkspace.css";
import { api } from "../api";
import { useUser } from "../UserContext";

function clean(value) {
  return value == null
    ? ""
    : String(value).trim();
}

function projectIdFromPath() {
  try {
    const path =
      String(
        window.location.pathname ||
        ""
      );

    const prefix =
      "/reader/screenplay/";

    if (
      !path
        .toLowerCase()
        .startsWith(prefix)
    ) {
      return "";
    }

    return decodeURIComponent(
      path.slice(prefix.length)
        .split("/")[0] ||
      ""
    ).trim();
  } catch {
    return "";
  }
}

function elementClass(type) {
  const normalized =
    clean(type)
      .toLowerCase()
      .replace(
        /_/g,
        "-"
      );

  return normalized
    ? `ns-screenplay-element-${normalized}`
    : "ns-screenplay-element-action";
}

function displayAuthor(reader) {
  return (
    clean(reader?.authorName) ||
    clean(reader?.authorUsername)
  );
}

function fallbackExportFileName(
  reader,
  profile,
  extension
) {
  const stem =
    (
      clean(
        reader?.projectTitle
      ) ||
      "screenplay"
    )
      .normalize("NFKD")
      .replace(
        /[\u0300-\u036f]/g,
        ""
      )
      .replace(
        /[^A-Za-z0-9]+/g,
        "-"
      )
      .replace(
        /^-+|-+$/g,
        ""
      )
      .slice(0, 80) ||
    "screenplay";

  const suffix =
    profile ===
      "published_reading_edition"
      ? "reading-edition"
      : "submission-copy";

  return `${stem}-${suffix}.${extension}`;
}

export default function ScreenplayReaderView() {
  const {
    isAuthenticated = false,
    booting = false,
  } = useUser() || {};

  const lang =
    (
      typeof window !==
        "undefined" &&
      window.localStorage
        .getItem("ns.lang")
    ) ||
    "en";

  const isFR =
    String(lang)
      .toLowerCase() ===
    "fr";

  const projectId =
    projectIdFromPath();

  const [
    reader,
    setReader,
  ] = useState(null);

  const [
    readerAccessMode,
    setReaderAccessMode,
  ] = useState("public");

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  const [
    exportOpen,
    setExportOpen,
  ] = useState(false);

  const [
    exportProfile,
    setExportProfile,
  ] = useState(
    "submission_copy"
  );

  const [
    exportFrontMatter,
    setExportFrontMatter,
  ] = useState({
    sourceOrAdaptationStatement:
      "",
    contactInformation:
      "",
    copyrightNotice:
      "",
    dedication:
      "",
    acknowledgements:
      "",
  });

  const [
    exportRights,
    setExportRights,
  ] = useState({
    basis: "",
    creatorConfirmed:
      false,
  });

  const [
    exporting,
    setExporting,
  ] = useState(false);

  const [
    exportingDocx,
    setExportingDocx,
  ] = useState(false);

  const [
    exportError,
    setExportError,
  ] = useState("");

  useEffect(
    () => {
      let active = true;

      async function load() {
        if (booting) {
          return;
        }

        if (!projectId) {
          setError(
            isFR
              ? "Identifiant de projet de scénario manquant."
              : "Missing Screenplay project identifier."
          );
          setLoading(false);
          return;
        }

        setLoading(true);
        setError("");

        try {
          let payload = null;
          let nextAccessMode =
            "public";

          if (isAuthenticated) {
            try {
              payload =
                await api
                  .getScreenplayReleasedReaderView(
                    projectId
                  );

              nextAccessMode =
                "workspace";
            } catch (_ownerError) {
              payload =
                await api
                  .getPublicScreenplayReaderView(
                    projectId
                  );
            }
          } else {
            payload =
              await api
                .getPublicScreenplayReaderView(
                  projectId
                );
          }

          if (!active) {
            return;
          }

          const next =
            payload?.readerView ||
            null;

          if (!next) {
            throw new Error(
              isFR
                ? "Aucun brouillon diffusé n’est disponible pour Reader View."
                : "No Released Draft is available for Reader View."
            );
          }

          setReaderAccessMode(
            nextAccessMode
          );

          if (
            nextAccessMode !==
              "workspace"
          ) {
            setExportOpen(false);
          }

          setReader(next);
        } catch (err) {
          if (!active) {
            return;
          }

          setReaderAccessMode(
            "public"
          );
          setExportOpen(false);
          setReader(null);
          setError(
            clean(
              err?.message
            ) ||
            (
              isFR
                ? "Impossible d’ouvrir Screenplay Reader View."
                : "Could not open Screenplay Reader View."
            )
          );
        } finally {
          if (active) {
            setLoading(false);
          }
        }
      }

      load();

      return () => {
        active = false;
      };
    },
    [
      booting,
      isAuthenticated,
      isFR,
      projectId,
    ]
  );

  const scenes =
    useMemo(
      () =>
        (
          Array.isArray(
            reader?.scenes
          )
            ? reader.scenes
            : []
        )
          .slice()
          .sort(
            (
              left,
              right
            ) =>
              Number(
                left?.order ||
                0
              ) -
              Number(
                right?.order ||
                0
              )
          ),
      [reader]
    );

  const author =
    displayAuthor(
      reader
    );

  const rightsNeedsSource =
    !!exportRights.basis &&
    exportRights.basis !==
      "original_work";

  const rightsReady =
    !!exportRights.basis &&
    exportRights
      .creatorConfirmed &&
    (
      !rightsNeedsSource ||
      !!clean(
        exportFrontMatter
          .sourceOrAdaptationStatement
      )
    );

  function returnToWorkspace() {
    if (!projectId) {
      window.location.href =
        "/my-projects/screenplay";
      return;
    }

    window.location.href =
      `/my-projects/screenplay/script-workspace?projectId=${encodeURIComponent(
        projectId
      )}`;
  }

  function returnToDiscovery() {
    window.location.href =
      "/reader/screenplay";
  }

  function setFrontMatterField(
    field,
    value
  ) {
    setExportFrontMatter(
      (current) => ({
        ...current,
        [field]:
          value,
      })
    );

    setExportError("");
  }

  async function downloadPdf() {
    if (
      exporting ||
      !projectId ||
      !rightsReady
    ) {
      return;
    }

    setExporting(true);
    setExportError("");

    const frontMatter =
      exportProfile ===
        "submission_copy"
        ? {
            sourceOrAdaptationStatement:
              exportFrontMatter
                .sourceOrAdaptationStatement,
            contactInformation:
              exportFrontMatter
                .contactInformation,
          }
        : {
            sourceOrAdaptationStatement:
              exportFrontMatter
                .sourceOrAdaptationStatement,
            copyrightNotice:
              exportFrontMatter
                .copyrightNotice,
            dedication:
              exportFrontMatter
                .dedication,
            acknowledgements:
              exportFrontMatter
                .acknowledgements,
          };

    try {
      const result =
        await api
          .downloadScreenplayPdf(
            projectId,
            {
              profile:
                exportProfile,
              frontMatter,
              rights: {
                basis:
                  exportRights
                    .basis,
                creatorConfirmed:
                  exportRights
                    .creatorConfirmed,
              },
            }
          );

      const url =
        window.URL
          .createObjectURL(
            result.blob
          );

      const anchor =
        document
          .createElement(
            "a"
          );

      anchor.href = url;

      const returnedFileName =
        clean(
          result.fileName
        );

      anchor.download =
        (
          returnedFileName &&
          returnedFileName
            .toLowerCase() !==
            "screenplay.pdf"
        )
          ? returnedFileName
          : fallbackExportFileName(
              reader,
              exportProfile,
              "pdf"
            );

      document.body
        .appendChild(
          anchor
        );

      anchor.click();
      anchor.remove();

      window.setTimeout(
        () =>
          window.URL
            .revokeObjectURL(
              url
            ),
        1000
      );
    } catch (err) {
      setExportError(
        clean(
          err?.message
        ) ||
        (
          isFR
            ? "Impossible d’exporter le PDF."
            : "Could not export the PDF."
        )
      );
    } finally {
      setExporting(false);
    }
  }

  async function downloadDocx() {
    if (
      exporting ||
      exportingDocx ||
      !projectId ||
      !rightsReady
    ) {
      return;
    }

    setExportingDocx(true);
    setExportError("");

    const frontMatter =
      exportProfile ===
        "submission_copy"
        ? {
            sourceOrAdaptationStatement:
              exportFrontMatter
                .sourceOrAdaptationStatement,
            contactInformation:
              exportFrontMatter
                .contactInformation,
          }
        : {
            sourceOrAdaptationStatement:
              exportFrontMatter
                .sourceOrAdaptationStatement,
            copyrightNotice:
              exportFrontMatter
                .copyrightNotice,
            dedication:
              exportFrontMatter
                .dedication,
            acknowledgements:
              exportFrontMatter
                .acknowledgements,
          };

    try {
      const result =
        await api
          .downloadScreenplayDocx(
            projectId,
            {
              profile:
                exportProfile,
              frontMatter,
              rights: {
                basis:
                  exportRights
                    .basis,
                creatorConfirmed:
                  exportRights
                    .creatorConfirmed,
              },
            }
          );

      const url =
        window.URL
          .createObjectURL(
            result.blob
          );

      const anchor =
        document
          .createElement(
            "a"
          );

      anchor.href = url;

      const returnedFileName =
        clean(
          result.fileName
        );

      anchor.download =
        (
          returnedFileName &&
          returnedFileName
            .toLowerCase() !==
            "screenplay.docx"
        )
          ? returnedFileName
          : fallbackExportFileName(
              reader,
              exportProfile,
              "docx"
            );

      document.body
        .appendChild(
          anchor
        );

      anchor.click();
      anchor.remove();

      window.setTimeout(
        () =>
          window.URL
            .revokeObjectURL(
              url
            ),
        1000
      );
    } catch (err) {
      setExportError(
        clean(
          err?.message
        ) ||
        (
          isFR
            ? "Impossible d’exporter le DOCX."
            : "Could not export the DOCX."
        )
      );
    } finally {
      setExportingDocx(false);
    }
  }

  if (loading) {
    return (
      <main
        className="page"
      >
        <div
          className="ns-card"
          style={{
            maxWidth: 980,
            margin: "0 auto",
            padding: 24,
          }}
        >
          {isFR
            ? "Ouverture du brouillon diffusé…"
            : "Opening Released Draft…"}
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main
        className="page"
      >
        <div
          className="ns-card"
          style={{
            maxWidth: 980,
            margin: "0 auto",
            padding: 24,
          }}
        >
          <div
            style={{
              color: "#b91c1c",
              fontWeight: 800,
            }}
          >
            {error}
          </div>

          <button
            type="button"
            className="ns-button ns-button-outline"
            style={{
              marginTop: 16,
            }}
            onClick={
              returnToDiscovery
            }
          >
            {isFR
              ? "Retour aux scénarios"
              : "Back to Screenplays"}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main
      className="page"
    >
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
        }}
      >
        <section
          className="ns-card"
          style={{
            padding: 22,
            marginBottom: 18,
          }}
        >
          <div
            style={{
              color: "#b45309",
              fontSize: 12,
              fontWeight: 900,
              letterSpacing:
                "0.11em",
              textTransform:
                "uppercase",
            }}
          >
            SCREENPLAY READER VIEW
          </div>

          <div
            style={{
              display: "flex",
              justifyContent:
                "space-between",
              alignItems:
                "flex-start",
              gap: 16,
              flexWrap:
                "wrap",
              marginTop: 7,
            }}
          >
            <div>
              <h1
                className="ns-section-title"
                style={{
                  margin:
                    "0 0 6px",
                }}
              >
                {clean(
                  reader
                    ?.projectTitle
                ) ||
                  (
                    isFR
                      ? "Scénario sans titre"
                      : "Untitled Screenplay"
                  )}
              </h1>

              {author ? (
                <div
                  className="ns-card-meta"
                  style={{
                    fontSize: 14,
                  }}
                >
                  {isFR
                    ? "Par"
                    : "By"}{" "}
                  <strong>
                    {author}
                  </strong>
                </div>
              ) : null}
            </div>

            <div
              style={{
                display:
                  "flex",
                gap: 8,
                flexWrap:
                  "wrap",
                justifyContent:
                  "flex-end",
              }}
            >
              {readerAccessMode ===
              "workspace" ? (
                <>
                  <button
                    type="button"
                    className="ns-button ns-button-primary"
                    aria-expanded={
                      exportOpen
                    }
                    onClick={() => {
                      setExportOpen(
                        (value) =>
                          !value
                      );
                      setExportError(
                        ""
                      );
                    }}
                  >
                    {exportOpen
                      ? (
                          isFR
                            ? "Fermer l’export"
                            : "Close export"
                        )
                      : (
                          isFR
                            ? "Exporter"
                            : "Export"
                        )}
                  </button>

                  <button
                    type="button"
                    className="ns-button ns-button-outline"
                    onClick={
                      returnToWorkspace
                    }
                  >
                    {isFR
                      ? "Retour au Script Workspace"
                      : "Back to Script Workspace"}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="ns-button ns-button-outline"
                  onClick={
                    returnToDiscovery
                  }
                >
                  {isFR
                    ? "Retour aux scénarios"
                    : "Back to Screenplays"}
                </button>
              )}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 9,
              flexWrap: "wrap",
              alignItems:
                "center",
              marginTop: 16,
            }}
          >
            <span
              className="ns-card-meta"
              style={{
                display:
                  "inline-flex",
                alignItems:
                  "center",
                padding:
                  "5px 9px",
                border:
                  "1px solid #fde68a",
                borderRadius:
                  999,
                background:
                  "#fffbeb",
                color:
                  "#92400e",
                fontWeight:
                  800,
              }}
            >
              {isFR
                ? `Brouillon diffusé · Révision ${reader.sourceDraftRevision}`
                : `Released Draft · Revision ${reader.sourceDraftRevision}`}
            </span>

            <span
              className="ns-card-meta"
            >
              {isFR
                ? `${scenes.length} scène${scenes.length === 1 ? "" : "s"}`
                : `${scenes.length} scene${scenes.length === 1 ? "" : "s"}`}
            </span>
          </div>

          <div
            className="ns-card-soft"
            style={{
              marginTop: 14,
              padding:
                "10px 12px",
              lineHeight: 1.5,
            }}
          >
            {readerAccessMode ===
            "workspace"
              ? (
                  isFR
                    ? "Vue de lecture contrôlée : elle affiche exactement le brouillon diffusé. Les modifications ou approbations ultérieures ne changent pas cette lecture. Ceci n’est pas une publication publique."
                    : "Controlled Reader View: this displays the exact Released Draft. Later Draft edits or approvals do not change this reading copy. This is not public publication."
                )
              : (
                  isFR
                    ? "Copie de lecture diffusée : cette vue affiche exactement la version que le créateur a rendue disponible à la lecture."
                    : "Released reading copy: this view displays exactly the version the creator made available for reading."
                )}
          </div>
          {readerAccessMode ===
          "workspace" &&
          exportOpen ? (
            <section
              aria-label={
                isFR
                  ? "Exporter le scénario"
                  : "Export Screenplay"
              }
              style={{
                marginTop: 16,
                padding: 16,
                border:
                  "1px solid rgba(5, 122, 85, 0.20)",
                borderRadius: 12,
                background:
                  "#f8fbf9",
              }}
            >
              <div
                style={{
                  fontWeight: 850,
                  marginBottom: 5,
                }}
              >
                {isFR
                  ? "Exporter le brouillon diffusé"
                  : "Export the Released Draft"}
              </div>

              <div
                className="ns-card-meta"
                style={{
                  lineHeight: 1.5,
                  marginBottom: 14,
                }}
              >
                {isFR
                  ? "Le corps du scénario provient uniquement du brouillon diffusé. Les informations ci-dessous sont utilisées pour ce fichier exporté et ne modifient pas le brouillon diffusé."
                  : "The screenplay body comes only from the Released Draft. The details below are used for this exported file and do not change the Released Draft."}
              </div>

              <label
                style={{
                  display: "grid",
                  gap: 6,
                  maxWidth: 520,
                }}
              >
                <strong>
                  {isFR
                    ? "Profil d’export"
                    : "Export profile"}
                </strong>

                <select
                  className="ns-input"
                  value={
                    exportProfile
                  }
                  onChange={(event) => {
                    setExportProfile(
                      event.target
                        .value
                    );
                    setExportError(
                      ""
                    );
                  }}
                >
                  <option
                    value="submission_copy"
                  >
                    {isFR
                      ? "Scénario / copie de soumission"
                      : "Screenplay / Submission Copy"}
                  </option>

                  <option
                    value="published_reading_edition"
                  >
                    {isFR
                      ? "Édition de lecture publiée"
                      : "Published Reading Edition"}
                  </option>
                </select>
              </label>

              <div
                className="ns-card-meta"
                style={{
                  marginTop: 8,
                  lineHeight: 1.5,
                }}
              >
                {exportProfile ===
                  "submission_copy"
                  ? (
                      rightsNeedsSource
                        ? (
                            isFR
                              ? "Page de titre conventionnelle, puis corps du scénario. Les coordonnées sont facultatives. Une déclaration de source/adaptation est requise pour la base des droits sélectionnée."
                              : "Conventional title page, then the screenplay body. Contact information is optional. A source/adaptation statement is required for the selected rights basis."
                          )
                        : (
                            isFR
                              ? "Page de titre conventionnelle, puis corps du scénario. Les coordonnées et la déclaration de source/adaptation sont facultatives."
                              : "Conventional title page, then the screenplay body. Contact information and a source/adaptation statement are optional."
                          )
                    )
                  : (
                      rightsNeedsSource
                        ? (
                            isFR
                              ? "Page de titre, déclaration de source/adaptation requise, autre matière préliminaire facultative, corps du scénario, puis remerciements facultatifs."
                              : "Title page, required source/adaptation statement, other optional front matter, screenplay body, then optional acknowledgements."
                          )
                        : (
                            isFR
                              ? "Page de titre, matière préliminaire facultative, corps du scénario, puis remerciements facultatifs."
                              : "Title page, optional front matter, screenplay body, then optional acknowledgements."
                          )
                    )}
              </div>

              <div
                style={{
                  display: "grid",
                  gap: 12,
                  marginTop: 14,
                }}
              >
                <label
                  style={{
                    display: "grid",
                    gap: 6,
                  }}
                >
                  <strong>
                    {isFR
                      ? "Base des droits"
                      : "Rights basis"}
                  </strong>

                  <select
                    className="ns-input"
                    value={
                      exportRights
                        .basis
                    }
                    onChange={(event) => {
                      setExportRights(
                        (current) => ({
                          ...current,
                          basis:
                            event.target
                              .value,
                        })
                      );
                      setExportError(
                        ""
                      );
                    }}
                  >
                    <option value="">
                      {isFR
                        ? "Choisir une base des droits"
                        : "Choose a rights basis"}
                    </option>

                    <option
                      value="original_work"
                    >
                      {isFR
                        ? "Œuvre originale"
                        : "Original work"}
                    </option>

                    <option
                      value="authorized_adaptation"
                    >
                      {isFR
                        ? "Adaptation autorisée"
                        : "Authorized adaptation"}
                    </option>

                    <option
                      value="other_lawful_basis"
                    >
                      {isFR
                        ? "Domaine public / autre base licite"
                        : "Public domain / other lawful basis"}
                    </option>
                  </select>
                </label>

                <div
                  className="ns-card-meta"
                  style={{
                    lineHeight: 1.5,
                  }}
                >
                  {isFR
                    ? "NextScenes ne vérifie pas la propriété ni les autorisations. La plateforme enregistre uniquement ce que vous confirmez pour cet export."
                    : "NextScenes does not verify ownership or permission. It records only what you confirm for this export."}
                </div>

                <label
                  style={{
                    display: "grid",
                    gap: 6,
                  }}
                >
                  <strong>
                    {isFR
                      ? (
                          rightsNeedsSource
                            ? "Source / adaptation — requis"
                            : "Source / adaptation — facultatif"
                        )
                      : (
                          rightsNeedsSource
                            ? "Source / adaptation — required"
                            : "Source / adaptation — optional"
                        )}
                  </strong>

                  <textarea
                    className="ns-input"
                    rows={3}
                    value={
                      exportFrontMatter
                        .sourceOrAdaptationStatement
                    }
                    onChange={(event) =>
                      setFrontMatterField(
                        "sourceOrAdaptationStatement",
                        event.target
                          .value
                      )
                    }
                    placeholder={
                      isFR
                        ? "Ex. Scénario original, ou adaptation de…"
                        : "e.g. Original screenplay, or adapted from…"
                    }
                  />
                </label>

                {rightsNeedsSource &&
                !clean(
                  exportFrontMatter
                    .sourceOrAdaptationStatement
                ) ? (
                  <div
                    className="ns-card-meta"
                    style={{
                      lineHeight: 1.5,
                      color:
                        "#92400e",
                    }}
                  >
                    {isFR
                      ? "Ajoutez une déclaration de source/adaptation pour cette base des droits."
                      : "Add a source/adaptation statement for this rights basis."}
                  </div>
                ) : null}

                <label
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems:
                      "flex-start",
                    lineHeight: 1.45,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={
                      exportRights
                        .creatorConfirmed
                    }
                    onChange={(event) => {
                      setExportRights(
                        (current) => ({
                          ...current,
                          creatorConfirmed:
                            event.target
                              .checked,
                        })
                      );
                      setExportError(
                        ""
                      );
                    }}
                  />

                  <span>
                    {isFR
                      ? "Je confirme que je suis responsable de la base des droits et des informations de source/adaptation fournies pour cet export."
                      : "I confirm that I am responsible for the rights basis and source/adaptation information supplied for this export."}
                  </span>
                </label>

                {exportProfile ===
                "submission_copy" ? (
                  <label
                    style={{
                      display: "grid",
                      gap: 6,
                    }}
                  >
                    <strong>
                      {isFR
                        ? "Coordonnées — facultatif"
                        : "Contact information — optional"}
                    </strong>

                    <textarea
                      className="ns-input"
                      rows={3}
                      value={
                        exportFrontMatter
                          .contactInformation
                      }
                      onChange={(event) =>
                        setFrontMatterField(
                          "contactInformation",
                          event.target
                            .value
                        )
                      }
                      placeholder={
                        isFR
                          ? "Coordonnées que vous souhaitez faire apparaître sur cette copie."
                          : "Contact details you want shown on this copy."
                      }
                    />
                  </label>
                ) : (
                  <>
                    <label
                      style={{
                        display: "grid",
                        gap: 6,
                      }}
                    >
                      <strong>
                        {isFR
                          ? "Avis de droits d’auteur — facultatif"
                          : "Copyright notice — optional"}
                      </strong>

                      <textarea
                        className="ns-input"
                        rows={3}
                        value={
                          exportFrontMatter
                            .copyrightNotice
                        }
                        onChange={(event) =>
                          setFrontMatterField(
                            "copyrightNotice",
                            event.target
                              .value
                          )
                        }
                        placeholder={
                          isFR
                            ? "Texte approuvé par le créateur uniquement."
                            : "Creator-approved wording only."
                        }
                      />
                    </label>

                    <label
                      style={{
                        display: "grid",
                        gap: 6,
                      }}
                    >
                      <strong>
                        {isFR
                          ? "Dédicace — facultatif"
                          : "Dedication — optional"}
                      </strong>

                      <textarea
                        className="ns-input"
                        rows={3}
                        value={
                          exportFrontMatter
                            .dedication
                        }
                        onChange={(event) =>
                          setFrontMatterField(
                            "dedication",
                            event.target
                              .value
                          )
                        }
                      />
                    </label>

                    <label
                      style={{
                        display: "grid",
                        gap: 6,
                      }}
                    >
                      <strong>
                        {isFR
                          ? "Remerciements — facultatif"
                          : "Acknowledgements — optional"}
                      </strong>

                      <textarea
                        className="ns-input"
                        rows={4}
                        value={
                          exportFrontMatter
                            .acknowledgements
                        }
                        onChange={(event) =>
                          setFrontMatterField(
                            "acknowledgements",
                            event.target
                              .value
                          )
                        }
                      />
                    </label>
                  </>
                )}
              </div>

              {exportError ? (
                <div
                  style={{
                    marginTop: 12,
                    color:
                      "#b91c1c",
                    fontWeight:
                      750,
                  }}
                >
                  {exportError}
                </div>
              ) : null}

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap:
                    "wrap",
                  marginTop: 14,
                }}
              >
                <button
                  type="button"
                  className="ns-button ns-button-primary"
                  disabled={
                    exporting ||
                    exportingDocx ||
                    !rightsReady
                  }
                  onClick={
                    downloadPdf
                  }
                >
                  {exporting
                    ? (
                        isFR
                          ? "Création du PDF…"
                          : "Creating PDF…"
                      )
                    : (
                        isFR
                          ? "Télécharger le PDF"
                          : "Download PDF"
                      )}
                </button>

                <button
                  type="button"
                  className="ns-button ns-button-outline"
                  disabled={
                    exporting ||
                    exportingDocx ||
                    !rightsReady
                  }
                  onClick={
                    downloadDocx
                  }
                >
                  {exportingDocx
                    ? (
                        isFR
                          ? "Création du DOCX…"
                          : "Creating DOCX…"
                      )
                    : (
                        isFR
                          ? "Télécharger le DOCX"
                          : "Download DOCX"
                      )}
                </button>
              </div>
            </section>
          ) : null}
        </section>

        <div
          className="ns-screenplay-draft-paper"
          aria-label={
            isFR
              ? "Scénario diffusé"
              : "Released Screenplay"
          }
        >
          {scenes.map(
            (
              scene,
              sceneIndex
            ) => {
              const elements =
                (
                  Array.isArray(
                    scene
                      ?.elements
                  )
                    ? scene.elements
                    : []
                )
                  .slice()
                  .sort(
                    (
                      left,
                      right
                    ) =>
                      Number(
                        left
                          ?.order ||
                        0
                      ) -
                      Number(
                        right
                          ?.order ||
                        0
                      )
                  );

              return (
                <section
                  key={
                    clean(
                      scene
                        ?.sceneId
                    ) ||
                    `scene-${sceneIndex + 1}`
                  }
                  className="ns-screenplay-scene is-reading"
                  aria-label={
                    isFR
                      ? `Scène ${sceneIndex + 1}`
                      : `Scene ${sceneIndex + 1}`
                  }
                >
                  <div
                    className="ns-screenplay-elements"
                  >
                    {elements.map(
                      (
                        element,
                        elementIndex
                      ) => (
                        <div
                          key={
                            clean(
                              element
                                ?.elementId
                            ) ||
                            `element-${sceneIndex + 1}-${elementIndex + 1}`
                          }
                          className={[
                            "ns-screenplay-element-row",
                            elementClass(
                              element
                                ?.type
                            ),
                          ].join(
                            " "
                          )}
                        >
                          <div
                            className="ns-screenplay-element-copy"
                          >
                            <div
                              className="ns-screenplay-element-readonly"
                            >
                              {element
                                ?.text ||
                                " "}
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </section>
              );
            }
          )}
        </div>
      </div>
    </main>
  );
}
